/**
 * Fontes enviadas pelo painel, registradas no navegador.
 *
 * As camadas de texto guardam só o NOME interno da fonte (`mbf-<id>`); é este
 * componente que ensina ao navegador de onde baixar cada uma. Fica na raiz do
 * app porque loja, painel e calibragem desenham os mesmos textos.
 */
import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import { customFontName, type CatalogFont } from "@montebox/shared/fonts";

import { API_URL, api } from "@/lib/api";

export interface UploadedFont extends CatalogFont {
  /** Caminho do arquivo na API. */
  file: string;
  /** Valor de `format()` no `@font-face`. */
  css: string;
  active: boolean;
}

export const fontKeys = { uploaded: ["catalog", "fonts"] as const };

export function useCatalogFonts(): UseQueryResult<UploadedFont[]> {
  return useQuery({
    queryKey: fontKeys.uploaded,
    queryFn: () => api.get<UploadedFont[]>("/catalog/fonts", { auth: false }),
    staleTime: 5 * 60_000,
  });
}

const SAFE_ID = /^[a-z0-9-]+$/;
const SAFE_FILE = /^\/catalog\/fonts\/[a-z0-9-]+\/file\?v=\d+$/;
const FORMATS = new Set(["truetype", "opentype", "woff", "woff2"]);

/**
 * Uma regra `@font-face` por fonte.
 *
 * Tudo que entra no CSS é conferido de novo aqui, mesmo vindo da nossa API: o
 * texto vai para uma folha de estilo, e uma resposta inesperada não pode virar
 * regra de CSS. O que não tem a forma esperada simplesmente não é registrado.
 */
const fontFaces = (fonts: UploadedFont[]): string =>
  fonts
    .filter((font) => SAFE_ID.test(font.id) && SAFE_FILE.test(font.file) && FORMATS.has(font.css))
    .map(
      (font) =>
        `@font-face{font-family:"${customFontName(font.id)}";src:url("${API_URL}${font.file}") format("${font.css}");font-display:swap;}`,
    )
    .join("\n");

export function CatalogFonts() {
  const css = fontFaces(useCatalogFonts().data ?? []);
  return css ? <style data-montebox-fonts="">{css}</style> : null;
}

/**
 * O navegador consegue abrir este arquivo como fonte?
 *
 * É o próprio sanitizador de fontes do navegador dizendo sim ou não, antes de
 * o arquivo sair da máquina do admin. O servidor confere de novo — isto só
 * poupa o envio de um arquivo que nenhum cliente conseguiria usar.
 */
export async function browserAcceptsFont(file: File): Promise<boolean> {
  if (typeof FontFace === "undefined") return true;
  try {
    await new FontFace("mb-font-check", await file.arrayBuffer()).load();
    return true;
  } catch {
    return false;
  }
}
